import {
  type InitAvo,
  normalizeAnalyticsEnvironment,
  registerAnalytics,
} from '@susu/analytics-client'

import { version } from '../../../package.json'
import Avo from '../../avo/avo'
import type { Environment } from '../config'
import { initDatadog } from '../datadog'

export interface PreferenceCenterAnalyticsConfig {
  /**
   * Whether this mount owns the page. Preference Center runs standalone on its
   * own app, and composed inside Customer Hub and the storefront. Defaults to
   * `false`, the safe direction.
   */
  ownsPage?: boolean
}

/** Suppresses default Avo `[avo] ...` console output from the generated client. */
const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

const initAvo: InitAvo = (
  options,
  systemProperties,
  destinationOptions,
  segmentDestination,
) => {
  Avo.initAvo(
    {
      env: options.env === 'prod' ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev,
      webDebugger: false,
      avoLogger: silentAvoLogger,
    },
    systemProperties as unknown as Parameters<typeof Avo.initAvo>[1],
    destinationOptions,
    segmentDestination as Parameters<typeof Avo.initAvo>[3],
  )
}

export const initializeAnalytics = (
  env: Environment,
  analyticsConfig: PreferenceCenterAnalyticsConfig = {},
): Promise<(() => void) | undefined> => {
  registerAnalytics({
    surface: {
      // A consumer-facing page: OneTrust gates the transport and the events go
      // to the Website source.
      placement: 'website',
      environment: normalizeAnalyticsEnvironment(env),
      // Embedded hosts (Customer Hub, the storefront) already own the
      // transport and the banner; only the standalone app is the page owner.
      ownsPage: analyticsConfig.ownsPage ?? false,
      // Applies only when this mount owns the page; a host keeps its own.
      pageType: 'preference_center',
    },
    applicationId: Avo.ApplicationId.PREFERENCE_CENTER,
    version,
    initAvo,
    setAvoSystemProperties: Avo.setSystemProperties as (
      properties: Record<string, unknown>,
    ) => void,
  })

  return initDatadog(env)
}
